import { Link } from "react-router-dom";
import {
  SHRINK_NETWORK_URL,
  SHRINKOPEDIA_URL,
  SHRINKTIONARY_URL,
  SHRINKDAILY_URL,
  DEPRESSION_RESOURCE_URL,
  ANXIETYRESEARCH_URL,
  PSYCHIATRYRX_URL,
  SHRINQ_URL,
  BEUNSTUCK_URL,
  QUITSCROLLING_URL,
  SHRINKMD_URL,
  SHRINKIATRY_URL,
  REVIEWER,
  networkUrl,
} from "@/lib/site";

/**
 * Reusable Shrink Network footer band.
 *
 * Drop-in component shared across every Shrink Network property. The link
 * list, column grouping, and copy are network-wide and identical on every
 * site; only the palette is themed to the host site via CSS variables
 * (--footer-bg, --footer-fg, --footer-muted, --footer-border, --brand) so
 * the band reads as native rather than pasted in.
 *
 * On the host property the self-link points to "/" (internal, same-tab);
 * every other property link opens in a new tab.
 */

interface NetworkProperty {
  name: string;
  url: string;
  internal?: boolean;
}

const COLUMNS: { header: string; items: NetworkProperty[] }[] = [
  {
    header: "Understand",
    items: [
      { name: "Shrinkopedia", url: SHRINKOPEDIA_URL },
      { name: "Shrinktionary", url: SHRINKTIONARY_URL },
      { name: "ShrinkDaily", url: SHRINKDAILY_URL },
      { name: "AnxietyResource", url: "/", internal: true },
      {
        name: "DepressionResource",
        url: DEPRESSION_RESOURCE_URL.replace(/\/$/, ""),
      },
      { name: "AnxietyResearch", url: ANXIETYRESEARCH_URL },
      { name: "PsychiatryRx", url: PSYCHIATRYRX_URL },
    ],
  },
  {
    header: "Apply",
    items: [
      { name: "shrinQ", url: SHRINQ_URL },
      { name: "Unstuck", url: BEUNSTUCK_URL },
      { name: "QuitScrolling", url: QUITSCROLLING_URL },
    ],
  },
  {
    header: "Care",
    items: [{ name: "shrinkMD", url: SHRINKMD_URL }],
  },
  {
    header: "Profession",
    items: [
      { name: "shrinkiatry", url: SHRINKIATRY_URL },
      { name: "Shariq Refai", url: REVIEWER.profileUrl },
    ],
  },
];

const BOTTOM_LINKS: { label: string; url: string }[] = [
  { label: "Network editorial standards", url: `${SHRINK_NETWORK_URL}/standards/` },
  { label: "Crisis and safety", url: `${SHRINK_NETWORK_URL}/crisis/` },
  { label: "About the network", url: `${SHRINK_NETWORK_URL}/` },
  { label: "Contact", url: `${SHRINK_NETWORK_URL}/contact/` },
];

export default function NetworkFooter() {
  const accentStyle = { color: "hsl(var(--brand))" } as const;
  const eyebrowClass =
    "text-xs font-semibold uppercase tracking-widest";
  const linkStyle = { color: "hsl(var(--footer-fg))" } as const;
  const mutedStyle = { color: "hsl(var(--footer-muted))" } as const;

  return (
    <div
      style={{
        background: "hsl(var(--footer-bg))",
        borderTop: "1px solid hsl(var(--footer-border))",
      }}
      aria-label="The Shrink Network"
    >
      <div className="mx-auto max-w-[1200px] px-6 py-12">
        {/* Top row: network mark, eyebrow, tagline */}
        <div className="flex items-center gap-3">
          <svg
            width="30"
            height="30"
            viewBox="0 0 30 30"
            role="img"
            aria-label="The Shrink Network mark"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M7 23 V14 C7 9 23 9 23 14 V23"
              fill="none"
              stroke="hsl(var(--brand))"
              strokeWidth="2.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <div className={eyebrowClass} style={accentStyle}>
            <a
              href={networkUrl(`${SHRINK_NETWORK_URL}/`, "network_footer")}
              target="_blank"
              rel="noopener noreferrer"
              style={accentStyle}
              className="hover:underline"
              data-testid="network-footer-shrink-network"
            >
              Part of The Shrink Network
            </a>
          </div>
        </div>
        <p
          className="mt-2 font-serif italic text-lg"
          style={linkStyle}
        >
          Your mind. Understood.
        </p>

        {/* Property grid */}
        <div className="mt-8 grid gap-8 sm:grid-cols-2 md:grid-cols-4">
          {COLUMNS.map((col) => (
            <div key={col.header}>
              <div className={`${eyebrowClass} mb-3`} style={accentStyle}>
                {col.header}
              </div>
              <ul className="space-y-2 text-sm list-none p-0 m-0">
                {col.items.map((item) => (
                  <li key={item.name}>
                    {item.internal ? (
                      <>
                        <Link
                          to={item.url}
                          style={linkStyle}
                          className="hover:underline"
                          data-testid="network-footer-anxietyresource"
                        >
                          {item.name}
                        </Link>
                        <span
                          className="ml-2 text-[10px] font-semibold uppercase tracking-wide"
                          style={accentStyle}
                          data-testid="network-footer-you-are-here"
                        >
                          You are here
                        </span>
                      </>
                    ) : (
                      <a
                        href={networkUrl(item.url, "network_footer")}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={linkStyle}
                        className="hover:underline"
                        data-testid={`network-footer-${new URL(item.url).hostname}`}
                      >
                        {item.name}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom: independence note, network-level links, network copyright */}
        <div
          className="mt-10 pt-6"
          style={{ borderTop: "1px solid hsl(var(--footer-border))" }}
        >
          <p className="text-xs leading-relaxed" style={mutedStyle}>
            <a
              href={networkUrl(`${SHRINK_NETWORK_URL}/`, "network_footer")}
              target="_blank"
              rel="noopener noreferrer"
              style={mutedStyle}
              className="hover:underline"
            >
              The Shrink Network
            </a>{" "}
            is independent. Each site is its own resource with its own purpose.
          </p>
          <p className="mt-2 text-xs leading-relaxed" style={mutedStyle}>
            {BOTTOM_LINKS.map((link, i) => (
              <span key={link.label}>
                {i > 0 && <span aria-hidden="true"> &middot; </span>}
                <a
                  href={networkUrl(link.url, "network_footer")}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={mutedStyle}
                  className="hover:underline"
                >
                  {link.label}
                </a>
              </span>
            ))}
          </p>
          <p className="mt-2 text-xs" style={mutedStyle}>
            &copy; 2026{" "}
            <a
              href={networkUrl(`${SHRINK_NETWORK_URL}/`, "network_footer")}
              target="_blank"
              rel="noopener noreferrer"
              style={mutedStyle}
              className="hover:underline"
            >
              The Shrink Network
            </a>
            .
          </p>
          <p
            className="mt-2 text-xs leading-relaxed"
            style={mutedStyle}
            data-testid="network-footer-trademark"
          >
            shrinkMD<sup>&reg;</sup> and shrinQ<sup>&reg;</sup> are registered
            trademarks of Shariq Refai, used with permission.
          </p>
        </div>
      </div>
    </div>
  );
}
